import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { FichaCaptacao } from '../types/ficha-captacao';
import { formatCurrency, formatPhone } from '../utils/masks';
import { Edit, ArrowLeft } from 'lucide-react';

interface FichaCaptacaoViewProps {
  ficha: FichaCaptacao;
  onEdit: () => void;
  onBack: () => void;
}

export const FichaCaptacaoView: React.FC<FichaCaptacaoViewProps> = ({
  ficha,
  onEdit,
  onBack
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Ficha de Captação</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={ficha?.status === 'definitivo' ? 'default' : 'secondary'}>
                {ficha?.status || 'rascunho'}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Criado em {new Date(ficha.created_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>
        </div>
        <Button onClick={onEdit}>
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados do Proprietário */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Dados do Proprietário</h3>
          <div className="space-y-3">
            <div>
              <span className="font-medium">Nome:</span> {ficha.proprietario_nome}
            </div>
            <div>
              <span className="font-medium">Telefone:</span> {formatPhone(ficha.proprietario_telefone)}
            </div>
            {ficha.proprietario_email && (
              <div>
                <span className="font-medium">Email:</span> {ficha.proprietario_email}
              </div>
            )}
            {ficha.proprietario_cpf_cnpj && (
              <div>
                <span className="font-medium">CPF/CNPJ:</span> {ficha.proprietario_cpf_cnpj}
              </div>
            )}
            {ficha.proprietario_endereco && (
              <div>
                <span className="font-medium">Endereço:</span> {ficha.proprietario_endereco}
              </div>
            )}
          </div>
        </Card>

        {/* Dados do Imóvel */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Dados do Imóvel</h3>
          <div className="space-y-3">
            <div>
              <span className="font-medium">Tipo:</span> {ficha.imovel_tipo}
            </div>
            <div>
              <span className="font-medium">Endereço:</span> {ficha.imovel_endereco}
              {ficha.imovel_numero && `, ${ficha.imovel_numero}`}
              {ficha.imovel_complemento && `, ${ficha.imovel_complemento}`}
            </div>
            <div>
              <span className="font-medium">Bairro:</span> {ficha.imovel_bairro}
            </div>
            <div>
              <span className="font-medium">Cidade/Estado:</span> {ficha.imovel_cidade} - {ficha.imovel_estado}
            </div>
            {ficha.imovel_cep && (
              <div>
                <span className="font-medium">CEP:</span> {ficha.imovel_cep}
              </div>
            )}
            {ficha.imovel_categoria && (
              <div>
                <span className="font-medium">Categoria:</span> {ficha.imovel_categoria}
              </div>
            )}
          </div>
        </Card>

        {/* Características */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Características</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium">Dormitórios:</span> {ficha.imovel_dormitorios}
            </div>
            <div>
              <span className="font-medium">Suítes:</span> {ficha.imovel_suites}
            </div>
            <div>
              <span className="font-medium">Banheiros:</span> {ficha.imovel_banheiros}
            </div>
            <div>
              <span className="font-medium">Vagas:</span> {ficha.imovel_vagas}
            </div>
            {ficha.imovel_area_construida && ficha.imovel_area_construida > 0 && (
              <div>
                <span className="font-medium">Área Construída:</span> {ficha.imovel_area_construida}m²
              </div>
            )}
            {ficha.imovel_area_total && ficha.imovel_area_total > 0 && (
              <div>
                <span className="font-medium">Área Total:</span> {ficha.imovel_area_total}m²
              </div>
            )}
            {ficha.imovel_ano_construcao && (
              <div>
                <span className="font-medium">Ano:</span> {ficha.imovel_ano_construcao}
              </div>
            )}
          </div>
        </Card>

        {/* Valores */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Valores</h3>
          <div className="space-y-3">
            {(ficha.valor_venda || 0) > 0 && (
              <div>
                <span className="font-medium">Valor de Venda:</span> {formatCurrency(ficha.valor_venda || 0)}
              </div>
            )}
            {(ficha.valor_locacao || 0) > 0 && (
              <div>
                <span className="font-medium">Valor de Locação:</span> {formatCurrency(ficha.valor_locacao || 0)}
              </div>
            )}
            {(ficha.condominio_valor || 0) > 0 && (
              <div>
                <span className="font-medium">Condomínio:</span> {formatCurrency(ficha.condominio_valor || 0)}
              </div>
            )}
            {(ficha.valor_iptu_anual || 0) > 0 && (
              <div>
                <span className="font-medium">IPTU Anual:</span> {formatCurrency(ficha.valor_iptu_anual || 0)}
              </div>
            )}
            {(ficha.valor_iptu_mensal || 0) > 0 && (
              <div>
                <span className="font-medium">IPTU Mensal:</span> {formatCurrency(ficha.valor_iptu_mensal || 0)}
              </div>
            )}
          </div>
        </Card>

        {/* Condições */}
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Condições e Situação</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${ficha.aceita_permuta ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              <span>Aceita Permuta</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${ficha.imovel_desocupado ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span>Imóvel Desocupado</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${ficha.aceita_financiamento ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              <span>Aceita Financiamento</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${ficha.esta_financiado ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
              <span>Está Financiado</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${ficha.pressa_venda ? 'bg-red-500' : 'bg-gray-300'}`}></div>
              <span>Pressa na Venda</span>
            </div>
          </div>
          
          {ficha.aceita_permuta && ficha.valor_permuta && ficha.valor_permuta > 0 && (
            <div className="mt-4">
              <span className="font-medium">Valor para Permuta:</span> {formatCurrency(ficha.valor_permuta)}
            </div>
          )}
          
          {ficha.esta_financiado && ficha.banco_financiador && (
            <div className="mt-4">
              <span className="font-medium">Banco Financiador:</span> {ficha.banco_financiador}
              {ficha.valor_financiado && ficha.valor_financiado > 0 && (
                <span className="ml-4">
                  <span className="font-medium">Valor Financiado:</span> {formatCurrency(ficha.valor_financiado)}
                </span>
              )}
            </div>
          )}
        </Card>

        {/* Observações */}
        {(ficha.motivo_venda || ficha.situacao_atual || ficha.observacoes || ficha.caracteristicas_especiais) && (
          <Card className="p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold mb-4">Observações</h3>
            <div className="space-y-4">
              {ficha.motivo_venda && (
                <div>
                  <span className="font-medium">Motivo da Venda:</span>
                  <p className="mt-1 text-muted-foreground">{ficha.motivo_venda}</p>
                </div>
              )}
              {ficha.situacao_atual && (
                <div>
                  <span className="font-medium">Situação Atual:</span>
                  <p className="mt-1 text-muted-foreground">{ficha.situacao_atual}</p>
                </div>
              )}
              {ficha.caracteristicas_especiais && (
                <div>
                  <span className="font-medium">Características Especiais:</span>
                  <p className="mt-1 text-muted-foreground">{ficha.caracteristicas_especiais}</p>
                </div>
              )}
              {ficha.observacoes && (
                <div>
                  <span className="font-medium">Observações Gerais:</span>
                  <p className="mt-1 text-muted-foreground">{ficha.observacoes}</p>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};